import { Deal, DealFeeType, DealListing } from '@/types/clientGroups';

export const DEFAULT_FLAT_FEE = 20_000;
export const FLAT_EXTENSION_FEE = 5_000;
export const DEFAULT_COMMISSION_RATE = 0.025;

export type DealFeePayload =
  | { feeType: 'Flat'; feeOverride: number | null }
  | { feeType: 'Percentage'; commissionRate: number };

export function isValidCommissionPercent(value: string | number): boolean {
  const numericPercent = Number(value);
  return Number.isFinite(numericPercent) && numericPercent > 0 && numericPercent <= 100;
}

export function buildDealFeePayload(
  feeType: DealFeeType,
  feeOverride: number | null,
  commissionPercent: string | number,
): DealFeePayload {
  if (feeType === 'Flat') return { feeType, feeOverride };

  const numericPercent = Number(commissionPercent);
  if (!isValidCommissionPercent(numericPercent)) {
    throw new RangeError('Commission rate must be greater than 0% and no more than 100%');
  }
  return { feeType, commissionRate: numericPercent / 100 };
}

const EXTENSION_NUMBER: Record<string, number> = { I: 1, II: 2, III: 3, IV: 4, V: 5 };

/** Mirrors the existing flat-fee arithmetic without changing its behavior. */
export function calculateAutomaticFlatFee(periodType: string): number {
  if (periodType === 'Initial') return DEFAULT_FLAT_FEE;
  const extension = periodType.replace('Ext ', '');
  return DEFAULT_FLAT_FEE + (EXTENSION_NUMBER[extension] || 1) * FLAT_EXTENSION_FEE;
}

export interface DealRevenueInput {
  feeType?: DealFeeType;
  periodType: string;
  feeOverride?: number | null;
  commissionRate?: number;
  targetPrices?: number[];
}

/** Returns null when a Percentage deal has no usable target-property price. */
export function calculateDealRevenue(input: DealRevenueInput): number | null {
  if ((input.feeType ?? 'Flat') === 'Flat') {
    return input.feeOverride ?? calculateAutomaticFlatFee(input.periodType);
  }

  const prices = (input.targetPrices ?? []).filter((price) => Number.isFinite(price) && price > 0);
  if (prices.length === 0 || !input.commissionRate) return null;
  const averagePrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
  return averagePrice * input.commissionRate;
}

export function calculateAggregateDealRevenue(deals: DealRevenueInput[]): number {
  return deals.reduce((sum, deal) => sum + (calculateDealRevenue(deal) ?? 0), 0);
}

const INACTIVE_TARGET_STATUSES = new Set(['Passed', 'Lost']);

export function getActiveTargetPrices(listings: DealListing[] = []): number[] {
  return listings
    .filter((listing) => !INACTIVE_TARGET_STATUSES.has(listing.status))
    .map((listing) => listing.propertyPrice)
    .filter((price): price is number => price !== undefined && price > 0);
}

export function calculateRevenueForDeal(deal: Deal): number | null {
  const contractedPrice = deal.listings
    ?.filter((listing) =>
      ['Under Contract', 'Pending', 'Escrow', 'Closed'].includes(listing.status),
    )
    .map((listing) => listing.acceptedPrice)
    .find((price): price is number => price !== undefined && price > 0);

  return calculateDealRevenue({
    feeType: deal.feeType,
    periodType: deal.periodType,
    feeOverride: deal.feeOverride,
    commissionRate: deal.commissionRate,
    targetPrices: contractedPrice ? [contractedPrice] : getActiveTargetPrices(deal.listings),
  });
}

export function calculateRevenueForDeals(deals: Deal[]): number {
  return deals.reduce((sum, deal) => sum + (calculateRevenueForDeal(deal) ?? 0), 0);
}
